export type RetourOrderUpdateRequest = {
  order_id: number;
  status: string;
  shipping_id: string;
  customer_name: string;
  customer_code: string;
  license_plate: string;
  transporter: string;
  retour_date: string;
  products: RetourOrderProductUpdateRequest[];
};

export type RetourOrderProductUpdateRequest = {
  id: string;
  amount: number;
};

export type RetourOrder = {
  id: string;
  status: string;
  retour_date: string;
  comments: Comment[];
  documents: Document[];
  products: RetourOrderProduct[];
  history: RetourStatusChange[];
  order_id: number;
  shipping_id: string;
  license_plate: string;
  transporter: string;
  checked_user_name: string;
  customer_name: string;
  customer_code: string;
};

export type ProductPlanningValidationRequest = {
  validations: ProductPlanningValidation[];
  delivery_date: string;
  original_delivery_date: string;
};

export type ProductPlanningValidation = {
  product_id: string;
  amount: number;
  original_amount: number;
};

export interface StyleObject {
  [key: string]: string;
}

export type AutomaticStockCount = {
  id: string;
  time: string;
};

export type StockCount = {
  id: string;
  status: string;
  product_code: string;
  product_name: string;
  created_user: string;
  created_user_id: string;
  count: number;
  product_pieces_per_pack: number;
  product_pieces_per_unit: number;
  product_weight: number;
  count_in_units: number;
  time: string;
  comments: Comment[];
  production_unit_name: string;
  automatic_stock_count?: AutomaticStockCount;
};

export type Account = {
  id: string;
  name: string;
  code: string;
  delivery_address_id: string;
  allow_partial_delivery: boolean;
  back_account_type: string;
  chamber_of_commerce_number: string;
  vat_number: string;
  classification_description: string;
  contact_person_id: string;
  creditor_number: string;
  debtor_number: string;
  customer_type_description: string;
  currency_code: string;
  email_address: string;
  package_receipt_email_addresses: string[];
  pickup_confirmation_email_addresses?: string[];
  automatically_send_pickup_confirmation?: boolean;
  invoice_email_addresses: string[];
  contact_person: ContactPerson;
  address: Address;
};

export type ContactPerson = {
  id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  gender: string;
};

export type Address = {
  id: string;
  phone: string;
  address: string;
  city: string;
  post_code: string;
  country: string;
  state_description: string;
  address_code: string;
  type: string;
};

export type User = {
  first_name: string;
  last_name: string;
  email: string;
};

export type ListUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  blocked: boolean;
};

export type Template = {
  id: string;
  name: string;
  content: string;
  type: number;
};

export type TemplatePreview = {
  content: string;
};

export type PickupOrder = {
  id: string;
  status: number;
  pickup_date: string;
  being_loaded_time: string;
  ready_for_pickup_time: string;
  cleanup_time: string;
  init_user: string;
  loading_user: string;
  cleanup_user: string;
  license_plate: string;
  transporter: string;
  sub_orders: PickupSubOrder[];
  comments: Comment[];
  history: StatusChange[];
  processing_error_msg: string;
  has_return_products: boolean;
  confirmation_email_sent?: boolean;
  account_transporters: AccountTransporter[];
};

export type AccountTransporter = {
  id: string;
  name: string;
  email_addresses: string[];
};

export type Comment = {
  id: string;
  message: string;
  timestamp: string;
  user_id: string;
  user_name: string;
};

export type StatusChange = {
  status: number;
  timestamp: string;
  user_id: string;
  user_name: string;
};

export type RetourStatusChange = {
  status: string;
  timestamp: string;
  user_id: string;
  user_name: string;
};

export type PickupOrderUpdate = {
  id: string;
  status: number;
  pickup_date: string;
  license_plate: string;
  transporter: string;
  transporter_id: string;
};

export type PickupSubOrder = {
  id: string;
  order_id: number;
  shipping_id: string;
  external_id: string;
  products: Product[];
  shipping_city: string;
  package_receipt_note: string;
  customer_name: string;
  pickup_order_pdf: string;
  pickup_order_pdf_comment: string;
};

export type PickupSubOrderUpdate = {
  id: string;
  order_id: number;
  shipping_id: string;
  external_id: string;
  products: Product[];
  shipping_city: string;
  package_receipt_note: string;
  customer_name: string;
  pickup_order_pdf_comment: string;
};

export type Product = {
  amount: number;
  id: string;
  code: string;
  product_id: string;
  description: string;
  product_type: number;
  amount_packs: number;
  weight: number;
  pieces_per_pack: number;
  pieces_per_unit: number;
  unit: string;
};

export type RetourOrderProduct = {
  amount: number;
  id: string;
  code: string;
  product_id: string;
  description: string;
  product_type: number;
  amount_packs: number;
  weight: number;
  pieces_per_pack: number;
  pieces_per_unit: number;
  unit: string;
  documents: Document[];
};

export enum DocumentType {
  Image = 'image/jpeg',
  PDF = 'application/pdf',
}

export type Document = {
  id: string;
  document: string;
  type: DocumentType;
};

export type ProductStockView = {
  amount: number;
  id: string;
  code: string;
  product_id: string;
  description: string;
  product_type: number;
  amount_packs: number;
  weight: number;
  pieces_per_pack: number;
  pieces_per_unit: number;
  unit: string;
  stock_amount: number;
  stock_type: number;
  limited_stock_amount: number;
  not_in_stock_amount: number;
  in_order_amount: number;
  in_order_amount_in_units: number;
  stock_status: number;
  delivery_over_time: DeliveryOverTime[];
  planned_production_over_time: DeliveryOverTime[];
  to_be_delivered: number;
  sales_order_limit: string[];
};

export type DeliveryOverTime = {
  date: string;
  amount: number;
  amount_in_units: number;
};

export type ProductExact = {
  description: string;
  item_code: string;
  quantity: number;
  quantity_delivered: number;
  sales_order_number: string;
};

export type ProdEdit = {
  amount: number;
  prod?: Product;
  index: number;
  stock_status: number;
};

export type RetourOrderProductCreateRequest = {
  id: string;
  amount: number;
};

export type PickupOrderProductCreateRequest = {
  amount: number;
  product_id: string;
  code: string;
  pieces_per_unit: number;
  unit: string;
};

export type RetourOrderDocument = {
  type: string;
  document: string;
};

export type RetourOrderCreateRequest = {
  order_id: number;
  shipping_id: string;
  customer_name: string;
  customer_code: string;
  license_plate: string;
  transporter: string;
  retour_date: string;
  check_directly: boolean;
  comment: string;
  products: RetourOrderProductCreateRequest[];
  documents: RetourOrderDocument[];
};

export type PickupOrderCreateReqeust = {
  order_id: number;
  shipping_id: string;
  external_id: string;
  pickup_date: string;
  shipping_city: string;
  customer_name: string;
  customer_code: string;
  status: number;
  products: PickupOrderProductCreateRequest[];
  pickup_order_pdf: string;
  pickup_order_pdf_comment: string;
};

export type PickupOrderList = PickupOrder[];

export type PickupOrderInvoice = {
  id: string;
  status: number;
  pickup_date: string;
  being_loaded_time: string;
  ready_for_pickup_time: string;
  cleanup_time: string;
  init_user: string;
  loading_user: string;
  cleanup_user: string;
  license_plate: string;
  transporter: string;
  processing_error_msg: string;
  account_transporters: AccountTransporter[];
  sub_orders: PickupSubOrderInvoice[];
  emails: Email[];
  comments: Comment[];
};

export type Email = {
  id: string;
  to: string[];
  subject: string;
  body: string;
  status: number;
};

export type PickupSubOrderInvoice = {
  id: string;
  order_id: number;
  shipping_id: string;
  external_id: string;
  shipping_city: string;
  customer_name: string;
  receipt_number: string;
  file_name: string;
  pdf: string;
  booking_status: number;
  products: Product[];
  received_booking_feedback_exact: boolean;
};

export type PickupOrderEmail = {
  pickup_order: PickupOrder;
  pdfs: PDF[];
  to: string[];
  subject: string;
  body: string;
  issue: string;
};

export type PDF = {
  data: string;
  file_name: string;
};

export function getOrderIDs(pickupOrder: PickupOrder) {
  const orderIDs: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (orderIDs.indexOf(val.order_id.toString()) === -1) {
      orderIDs.push(val.order_id.toString());
    }
  });

  return orderIDs.join(', ');
}

export function getCustomerNames(pickupOrder: PickupOrder) {
  const customerNames: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (customerNames.indexOf(val.customer_name.toString()) === -1) {
      customerNames.push(val.customer_name.toString());
    }
  });

  return customerNames.join(', ');
}

export function getShippingIDs(pickupOrder: PickupOrder) {
  const shippingIDs: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (shippingIDs.indexOf(val.shipping_id.toString()) === -1) {
      shippingIDs.push(val.shipping_id.toString());
    }
  });

  return shippingIDs.join(', ');
}

export function getShippingCities(pickupOrder: PickupOrder) {
  const shippingCities: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (shippingCities.indexOf(val.shipping_city.toString()) === -1) {
      shippingCities.push(val.shipping_city.toString());
    }
  });

  return shippingCities.join(', ');
}

export function getExternalIDs(pickupOrder: PickupOrder) {
  const externalIDs: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (externalIDs.indexOf(val.external_id.toString()) === -1) {
      externalIDs.push(val.external_id.toString());
    }
  });

  return externalIDs.join(', ');
}

export type ConfirmEvent = {
  title: string;
  description: string;
  confirm_event: string;
  with_checkbox: boolean;
  checkbox_text: string;
};

export type Claims = {
  sub: string;
};
